/**
 * Utilitários para extração de coordenadas geográficas de objetos de propriedade
 */

import GeometryService from '../../services/GeometryService';
const DEBUG = import.meta.env.VITE_DEBUG === 'true';
const VERBOSE = import.meta.env.VITE_DEBUG_VERBOSE === 'true';

/**
 * Função auxiliar para extrair coordenadas geográficas (latitude e longitude)
 * @param {Object} propertyData - Dados da propriedade diretamente do JSON do backend
 * @returns {Object|null} Objeto com lat e lng ou null se não encontrado
 */
export const extractCoordinates = (propertyData) => {
  try {
    // eslint-disable-next-line no-console
    if (VERBOSE) console.log('[extractCoordinates] Iniciando extração de coordenadas');
    if (!propertyData?.properties) {
      if (DEBUG) console.warn('[extractCoordinates] Aviso: propertyData.properties não existe');
      return null;
    }

    // 1. PRIORIDADE: Calcular centroide da geometria da área do imóvel do CAR
    if (propertyData.properties.car && Array.isArray(propertyData.properties.car)) {
      for (const carItem of propertyData.properties.car) {
        // Verificar se tem geometria com coordenadas para calcular o centroide
        if (
          carItem.geometry &&
          (carItem.geometry.type === 'Polygon' || carItem.geometry.type === 'MultiPolygon') &&
          Array.isArray(carItem.geometry.coordinates)
        ) {
          // Dependendo do tipo, processar diferentemente
          if (carItem.geometry.type === 'Polygon') {
            // Para polígonos simples
            const outerRing = carItem.geometry.coordinates[0]; // Anel externo do polígono
            if (Array.isArray(outerRing) && outerRing.length > 0) {
              let sumLng = 0;
              let sumLat = 0;
              let validPoints = 0;

              for (const point of outerRing) {
                if (Array.isArray(point) && point.length >= 2) {
                  // Em GeoJSON, formato é [lng, lat]
                  sumLng += Number(point[0]);
                  sumLat += Number(point[1]);
                  validPoints++;
                }
              }

              if (validPoints > 0) {
                return {
                  lat: sumLat / validPoints,
                  lng: sumLng / validPoints,
                };
              }
            }
          } else if (carItem.geometry.type === 'MultiPolygon') {
            // Para múltiplos polígonos, calculamos para o primeiro
            if (
              Array.isArray(carItem.geometry.coordinates[0]) &&
              Array.isArray(carItem.geometry.coordinates[0][0])
            ) {
              const outerRing = carItem.geometry.coordinates[0][0]; // Primeiro polígono, anel externo

              if (Array.isArray(outerRing) && outerRing.length > 0) {
                let sumLng = 0;
                let sumLat = 0;
                let validPoints = 0;

                for (const point of outerRing) {
                  if (Array.isArray(point) && point.length >= 2) {
                    sumLng += Number(point[0]);
                    sumLat += Number(point[1]);
                    validPoints++;
                  }
                }

                if (validPoints > 0) {
                  return {
                    lat: sumLat / validPoints,
                    lng: sumLng / validPoints,
                  };
                }
              }
            }
          }
        }

        // Se não há geometria completa, tentar obter do campo centroide
        if (carItem.details && carItem.details.centroide) {
          const centroide = carItem.details.centroide;
          if (typeof centroide === 'object' && centroide?.lat && centroide?.lng) {
            return {
              lat: centroide.lat,
              lng: centroide.lng,
            };
          }

          if (Array.isArray(centroide) && centroide.length >= 2) {
            return {
              lat: centroide[1], // Em GeoJSON, formato é [lng, lat]
              lng: centroide[0],
            };
          }
        }
      }
    }

    // 2. FALLBACK: Usar primeira parcela_co do codigo_imo (SIGEF)
    if (propertyData.properties.sigef && Array.isArray(propertyData.properties.sigef)) {
      for (const sigefItem of propertyData.properties.sigef) {
        // Tentar obter da primeira parcela
        if (
          sigefItem.parcela_co &&
          Array.isArray(sigefItem.parcela_co) &&
          sigefItem.parcela_co.length > 0
        ) {
          const parcela = sigefItem.parcela_co[0];
          if (parcela.ponto_ref) {
            return {
              lat: parcela.ponto_ref.latitude || parcela.ponto_ref.lat,
              lng: parcela.ponto_ref.longitude || parcela.ponto_ref.lng,
            };
          }
          // Outros formatos de dados de coordenadas na parcela
          if (parcela.coordinates || parcela.coordenadas) {
            const coords = parcela.coordinates || parcela.coordenadas;
            if (Array.isArray(coords) && coords.length > 0) {
              if (coords[0].latitude || coords[0].lat) {
                return {
                  lat: coords[0].latitude || coords[0].lat,
                  lng: coords[0].longitude || coords[0].lng,
                };
              } else if (Array.isArray(coords[0]) && coords[0].length >= 2) {
                return {
                  lat: coords[0][1], // GeoJSON [lng, lat]
                  lng: coords[0][0],
                };
              }
            }
          }
        }

        // Tentar obter de campos específicos do SIGEF
        if (sigefItem.centroide) {
          if (
            typeof sigefItem.centroide === 'object' &&
            sigefItem.centroide?.lat &&
            sigefItem.centroide?.lng
          ) {
            return {
              lat: sigefItem.centroide.lat,
              lng: sigefItem.centroide.lng,
            };
          }

          if (Array.isArray(sigefItem.centroide) && sigefItem.centroide.length >= 2) {
            return {
              lat: sigefItem.centroide[1], // GeoJSON [lng, lat]
              lng: sigefItem.centroide[0],
            };
          }
        }

        // Verificar outras estruturas de geometria no SIGEF
        if (
          sigefItem.geometry &&
          (sigefItem.geometry.type === 'Polygon' || sigefItem.geometry.type === 'MultiPolygon') &&
          Array.isArray(sigefItem.geometry.coordinates)
        ) {
          const coordinates =
            sigefItem.geometry.type === 'Polygon'
              ? sigefItem.geometry.coordinates[0]
              : sigefItem.geometry.coordinates[0][0];

          if (Array.isArray(coordinates) && coordinates.length > 0) {
            let sumLng = 0;
            let sumLat = 0;
            let validPoints = 0;

            for (const point of coordinates) {
              if (Array.isArray(point) && point.length >= 2) {
                sumLng += Number(point[0]);
                sumLat += Number(point[1]);
                validPoints++;
              }
            }

            if (validPoints > 0) {
              return {
                lat: sumLat / validPoints,
                lng: sumLng / validPoints,
              };
            }
          }
        }
      }
    }

    // Se chegamos até aqui, tenta usar GeometryService.calculateCentroid
    if (VERBOSE)
      if (propertyData && propertyData.properties) {
        // Processar as geometrias da propriedade com GeometryService
        const { geometries } = GeometryService.processPropertyGeometries(propertyData);

        if (geometries && geometries.length > 0) {
          // Calcular o centroide usando GeometryService
          const centroid = GeometryService.calculateCentroid(geometries);

          if (centroid) {
            if (VERBOSE) return centroid;
          }
        }
      }

    // Se mesmo com GeometryService não foi possível, usar fallback
    if (DEBUG)
      console.warn(
        '[extractCoordinates] Não foi possível calcular o centroide. Usando coordenadas fallback.',
      );
    return {
      lat: -15.798889, // Brasília - mesmo fallback do GeometryService
      lng: -47.866667,
    };
  } catch (error) {
    if (DEBUG) console.error('[extractCoordinates] Erro ao extrair coordenadas:', error);
    return null;
  }
};
